import { useRef, useState, type ComponentProps, type ReactNode, type RefObject } from 'react';
import { Checklist } from './Checklist';
import { ErrorNote, SectionHeading, UndoNote } from './ui';

/** Everything Checklist takes, minus the parts this supplies for itself. */
type PickerProps = Omit<
  ComponentProps<typeof Checklist>,
  'picked' | 'onPick' | 'focusRef' | 'children'
>;

/**
 * One relationship, in both of its halves: what is attached now, and a picker
 * for attaching more.
 *
 * A class's periods, its staff and its roll were three copies of this, and the
 * record pages for a student, a period and a staff member want three more. What
 * was copied is all scaffolding and no meaning: the ticked set and its reducer,
 * the panel ref focus returns to after applying, the form that ignores an empty
 * submit, and the apply button that counts the ticks.
 *
 * The standing list stays the caller's, passed as children. Only the caller
 * knows whether one of its rows is dragged into order, ended on a date, or
 * simply detached, and a row renderer general enough to express all three would
 * be worth less than the three plain lists it replaced.
 *
 * `query` stays controlled by the caller, matching Checklist's own reason for
 * it: the periods picker renders Create "<query>" underneath the options and
 * has to read what was typed.
 */
export function Related({
  title,
  description,
  count,
  children,
  error,
  undo,
  verb,
  actions,
  note,
  onApply,
  applying = false,
  focusRef,
  ...picker
}: {
  /** The section heading, e.g. "Marked across". */
  title: string;
  description?: string;
  /** The badge beside the heading, e.g. "4 columns". */
  count?: ReactNode;
  /** The standing list: what is attached right now. */
  children?: ReactNode;
  /**
   * A failed save. It sits above the picker, inside this section, rather than
   * at the top of a page that may be long enough to hide it.
   */
  error?: string | null;
  /** The last removal, while it can still be taken back. */
  undo?: { message: string; onUndo: () => void; onDismiss: () => void } | null;
  /** The apply button's verb: "Attach", "Assign", "Enrol". */
  verb: string;
  /** Controls beside the apply button, e.g. an enrolment's start date. */
  actions?: ReactNode;
  /** Anything under the action row, such as an error from a second mutation. */
  note?: ReactNode;
  /**
   * Applies the ticked ids. Call `done` once the save has actually succeeded:
   * it clears the ticks and puts focus back on the panel. Applying takes the
   * ticked rows out of the list, so without that a keyboard user is dropped on
   * <body> at the top of the document.
   */
  onApply: (ids: string[], done: () => void) => void;
  applying?: boolean;
  /**
   * The panel focus returns to. Supplied here only when the caller has a second
   * path that finishes an apply without going through `onApply`: attaching a
   * period can also create one first, and that path has to land focus in the
   * same place. Otherwise leave it out and this owns the ref.
   */
  focusRef?: RefObject<HTMLDivElement | null>;
} & PickerProps) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const own = useRef<HTMLDivElement>(null);
  const panel = focusRef ?? own;

  const done = () => {
    setPicked(new Set());
    panel.current?.focus();
  };

  return (
    <section>
      <SectionHeading title={title} description={description}>
        {count === undefined ? null : <span className="eyebrow">{count}</span>}
      </SectionHeading>

      {children}

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {undo ? (
        <div className="mb-4">
          <UndoNote message={undo.message} onUndo={undo.onUndo} onDismiss={undo.onDismiss} />
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (picked.size === 0) return;
          onApply([...picked], done);
        }}
      >
        <Checklist
          {...picker}
          picked={picked}
          onPick={(id, on) =>
            setPicked((prev) => {
              const next = new Set(prev);
              if (on) next.add(id);
              else next.delete(id);
              return next;
            })
          }
          focusRef={panel}
        >
          <div className="mt-3 flex flex-wrap items-end gap-3">
            {actions}
            <button
              className="btn btn-primary"
              type="submit"
              disabled={picked.size === 0 || applying}
            >
              {picked.size === 0 ? verb : `${verb} ${picked.size} selected`}
            </button>
          </div>
          {note}
        </Checklist>
      </form>
    </section>
  );
}
